import { array } from 'fp-ts';
import { flow } from 'fp-ts/function';
import { PayworksReceiptData, ReceiptType, StripeReceiptData } from '@mews-commander/data-access';
import { TextKey } from '@mews-commander/enums';
import { DividerList } from '@mews-commander/ui';
import { UnicodeEntity } from '@mews/enums';
import { LocalizedText } from '@mews/framework';
import { Typography } from '@optimus-web/core';
import { usePaymentDetailsSidebarContext } from '../context/PaymentDetailsSidebarContext';
import {
    PaymentDetailsCustomEntry,
    PaymentDetailsEntry,
    PaymentDetailsLocalizedEntry,
    PaymentDetailsSidebarGrid,
} from './PaymentDetailsSidebarGrid';
import { PaymentDetailsSidebarRowTypography } from './PaymentDetailsSidebarRow';

const getStripeReceiptEntries = (data: StripeReceiptData) => {
    const { terminal, entryMode, name, address, postalCode, city, country, authorizationCode, responseCode } = data;
    return (
        <DividerList spacing="300">
            <PaymentDetailsSidebarGrid>
                <PaymentDetailsSidebarRowTypography
                    labelTextKey={TextKey.AuthorizationCode}
                    value={authorizationCode}
                />
                <PaymentDetailsSidebarRowTypography labelTextKey={TextKey.ResponseCode} value={responseCode} />
                <PaymentDetailsSidebarRowTypography labelTextKey={TextKey.EntryMode} value={entryMode} />
                <PaymentDetailsSidebarRowTypography
                    labelTextKey={TextKey.PaymentDetailSidebarTerminalId}
                    value={terminal}
                />
            </PaymentDetailsSidebarGrid>
            <PaymentDetailsSidebarGrid>
                <PaymentDetailsSidebarRowTypography labelTextKey={TextKey.Name} value={name} />
                <PaymentDetailsSidebarRowTypography labelTextKey={TextKey.Address} value={address} />
                <PaymentDetailsSidebarRowTypography labelTextKey={TextKey.PostalCode} value={postalCode} />
                <PaymentDetailsSidebarRowTypography labelTextKey={TextKey.City} value={city} />
                <PaymentDetailsSidebarRowTypography labelTextKey={TextKey.Country} value={country} />
            </PaymentDetailsSidebarGrid>
        </DividerList>
    );
};

export const renderDetailEntries = flow(
    array.map((entry: PaymentDetailsLocalizedEntry | PaymentDetailsCustomEntry) => (
        <PaymentDetailsEntry {...entry} key={'textKey' in entry ? entry.textKey : entry.customLabel} />
    ))
);

const mapPayworksReceiptEntries = (data: PayworksReceiptData['data']) =>
    Object.entries(data).map(([key, value]) => ({ customLabel: key, value }));

const getPayworksReceiptEntries = (data: PayworksReceiptData) => {
    return (
        <>
            {renderDetailEntries(mapPayworksReceiptEntries(data.data))}
            {data.signatureLineRequired && (
                <Typography textStyle="bodyMediumStrong" as="div">
                    <LocalizedText textKey={TextKey.Signature} />
                    {UnicodeEntity.Colon}
                </Typography>
            )}
        </>
    );
};

const getPlainReceiptEntries = (value: string) => {
    return (
        <PaymentDetailsSidebarGrid>
            <PaymentDetailsSidebarRowTypography labelTextKey={TextKey.Receipt} value={value} />
        </PaymentDetailsSidebarGrid>
    );
};

export const TerminalDetailsSection = () => {
    const { receipt } = usePaymentDetailsSidebarContext();

    if (!receipt?.receiptData) {
        return null;
    }

    const { discriminator, value } = receipt.receiptData;

    switch (discriminator) {
        case ReceiptType.StripeReceipt:
            return getStripeReceiptEntries(value);
        case ReceiptType.PayworksReceipt:
            return getPayworksReceiptEntries(value);
        case ReceiptType.PlainReceipt:
            return getPlainReceiptEntries(value);
        default:
            return null;
    }
};
